import { defineStore, storeToRefs } from "pinia"
import { ref, computed } from "vue"
import { useApiStore } from "@/stores/api"
import { useSettingsStore } from "@/stores/settings"
import { deepEqual } from "@/helpers/deepequal"
import { dayjs } from "@/helpers/dayjs"

export const useTrackerStore = defineStore(
	"tracker",
	() => {
		const api = useApiStore()
		const settings = useSettingsStore()

		const trackers = ref([])
		const alarmHistoryLoaded = ref([])
		const { alarmHistorySeenUntil, alarmHistorySeenIds } = storeToRefs(useSettingsStore())

		const reset = () => {
			trackers.value = []
		}

		const loadTrackers = async () => {
			try {
				const response = await api.dispatchApiCall("get", `/api/v2/trackers`)
				if (!deepEqual(trackers.value, response.data)) {
					trackers.value = response.data
				}
				settings.validate()
				return true
			} catch (e) {
				return false
			}
		}

		const loadTrackerOvernights = async (trackerId, from, to) => {
			const response = await api.dispatchApiCall("get", `/api/v2/trackers/${trackerId}/overnights`, {
				limitTimeFrom: from,
				limitTimeTo: to,
			})
			return response.data
		}

		const loadTrackerStatistics = async (trackerId) => {
			const response = await api.dispatchApiCall("get", `/api/v2/trackers/${trackerId}/statistics`)
			return response.data
		}

		const loadTrackerTelemetry = async (trackerId, from, to) => {
			const response = await api.dispatchApiCall("get", `/api/v2/trackers/${trackerId}/telemetry`, {
				from: from,
				to: to,
			})
			return response.data
		}

		const loadTrackerTelemetryLatest = async (trackerId) => {
			const response = await api.dispatchApiCall("get", `/api/v2/trackers/${trackerId}/telemetry/latest`)
			return response.data
		}

		const updateTracker = async (data) => {
			return api.dispatchApiCall("patch", `/api/v2/trackers/${data.id}`, data)
		}

		const updateAlarmConfiguration = async (trackerId, alarmConfiguration) => {
			return api.dispatchApiCall("put", `/api/v2/trackers/${trackerId}/alarm_config`, alarmConfiguration)
		}

		const testAlarm = async () => {
			return api.dispatchApiCall("post", `/api/v2/session/test_alarm`)
		}

		const loadAlarmHistory = async () => {
			try {
				const response = await api.dispatchApiCall("get", `/api/v2/alarms`)
				alarmHistoryLoaded.value = response.data
				return true
			} catch (e) {
				return false
			}
		}

		/* access helpers */

		const trackersById = computed(() => {
			let returnArray = []
			trackers.value?.forEach((tracker) => {
				returnArray[tracker.id] = tracker
			})
			return returnArray
		})

		const anyAlarmEnabled = computed(() => {
			let returnValue = false
			trackers.value?.forEach((tracker) => {
				if (tracker.alarm_configuration.enabled) {
					returnValue = true
				}
			})
			return returnValue
		})

		const membershipEndsSoon = (tracker) => {
			return dayjs.utc(tracker.membership_ends_at).diff(dayjs(), "days") < 7
		}

		const anyMembershipEndsSoon = computed(() => {
			return trackers.value?.some((tracker) => membershipEndsSoon(tracker)) ?? false
		})

		const membershipEnded = (tracker) => {
			return dayjs.utc(tracker.membership_ends_at) < dayjs()
		}

		const anyMembershipEnded = computed(() => {
			return trackers.value?.some((tracker) => membershipEnded(tracker)) ?? false
		})

		const alarmHistorySeenUntilDate = computed(() => {
			const settingsStore = useSettingsStore()
            const val = settingsStore.alarmHistorySeenUntil
            return val ? dayjs.utc(val) : null
		})

		const alarmHistorySeenIdsSet = computed(() => {
            const settingsStore = useSettingsStore()
            return new Set(settingsStore.alarmHistorySeenIds || [])
        })

		const alarmIsSeen = (alarm) => {
			if (!alarm) return false

            if (alarmHistorySeenUntilDate.value && alarmHistorySeenUntilDate.value.isValid?.()) {
                if (dayjs.utc(alarm.inserted_at).isBefore(alarmHistorySeenUntilDate.value)) {
                    return true
                }
            }

            if (alarmHistorySeenIdsSet.value.has(alarm.id)) {
                return true
            }

			return false
		}

		const alarmHistory = computed(() => {
			return alarmHistoryLoaded.value.map((alarm) => {
				return {...alarm, seen: alarmIsSeen(alarm)}
			})
		})

		const alarmHistoryUnseen = computed(() => {
			return alarmHistory.value.filter((alarm) => !alarm.seen)
		})

		return {
			reset,
			loadTrackers,
			loadTrackerOvernights,
			loadTrackerStatistics,
			loadTrackerTelemetry,
			loadTrackerTelemetryLatest,
			trackers,
			trackersById,
			updateTracker,
			updateAlarmConfiguration,
			testAlarm,
			anyAlarmEnabled,
			anyMembershipEndsSoon,
			anyMembershipEnded,
			membershipEnded,
			membershipEndsSoon,
			loadAlarmHistory,
			alarmHistoryLoaded,
			alarmHistory,
			alarmHistoryUnseen
		}
	},
	{
		persist: true,
	},
)
